import { daysBetween, FITNESS_DAYS } from '@painpal/core';
import type { FormZone } from '@painpal/contracts';
import { formatInt, formatWeekdayMonthDay } from '../../lib/format';
import { plural } from '../../lib/plural';
import type { TodayStats } from './fitnessStats';

export interface HeadlineCopy { first: string; second: string; note: string | null }

// Fresh with Fitness at least this much below a week ago means Form is rising because training stopped.
export const DETRAINING_DROP = 1;

const HEADLINES: Record<FormZone | 'importing' | 'rebuilding' | 'detraining', HeadlineCopy> = {
  importing: {
    first: 'IMPORTING RIDES,', second: 'KEEP RIDING.',
    note: 'Form reads low until your older rides arrive, because Fitness needs some history.',
  },
  rebuilding: {
    first: 'RECALCULATING,', second: 'HANG TIGHT.',
    note: 'Your numbers are updating after a change. This usually takes under a minute.',
  },
  detraining: {
    first: 'RESTED, BUT', second: 'LOSING FITNESS.',
    note: 'Form is up because Fitness and Fatigue are both falling. Ride regularly to hold what you’ve built.',
  },
  fresh: {
    first: 'FRESH LEGS,', second: 'GO FAST.',
    note: 'Fatigue is well below Fitness. A good day for a race or a hard effort.',
  },
  neutral: {
    first: 'HOLDING STEADY.', second: 'ADD LOAD TO BUILD.',
    note: 'Fitness and Fatigue are balanced. Ride a little more or a little harder to start building.',
  },
  productive: {
    first: 'BUILDING', second: 'FITNESS.',
    note: 'Fatigue is above Fitness, so feeling tired is normal here. Plan an easy week every 3–4 weeks.',
  },
  overreaching: {
    first: 'EASE OFF,', second: 'RECOVER.',
    note: 'Fatigue is far above Fitness. A few easy days now cost less than illness or injury later.',
  },
};

export const EMPTY_HEADLINE: HeadlineCopy = { first: 'FIRST RIDE,', second: 'FIRST POINT.', note: null };

export interface HeadlineInput {
  stats: TodayStats | null;
  today: string;
  importing: boolean;
  rebuilding: boolean;
  historyStart: string | null;
}

export function historyDay(historyStart: string, today: string): number {
  return daysBetween(historyStart, today) + 1;
}

// Fitness averages FITNESS_DAYS of load, so until a user has that much history Fatigue outruns it and Form reads too low.
function learning(day: number): HeadlineCopy {
  return {
    first: 'STILL LEARNING,', second: 'KEEP RIDING.',
    note: `Day ${day} of ${FITNESS_DAYS}. Fitness takes about six weeks of rides to settle, so Form reads lower than it should until then.`,
  };
}

const losingFitness = ({ today, weekAgo }: TodayStats): boolean => weekAgo !== null && weekAgo.fitness - today.fitness >= DETRAINING_DROP;

export function fitnessHeadline({ stats, today, importing, rebuilding, historyStart }: HeadlineInput): HeadlineCopy {
  if (stats === null) return EMPTY_HEADLINE;
  if (importing) return HEADLINES.importing;
  if (rebuilding) return HEADLINES.rebuilding;
  const day = historyStart === null ? null : historyDay(historyStart, today);
  if (day !== null && day <= FITNESS_DAYS) return learning(day);
  if (stats.zone === 'fresh' && losingFitness(stats)) return HEADLINES.detraining;
  return HEADLINES[stats.zone];
}

export const ZONE_LABELS: Record<FormZone, string> = {
  fresh: 'FRESH', neutral: 'NEUTRAL', productive: 'PRODUCTIVE', overreaching: 'OVERREACHING',
};

export function fitnessEyebrow(today: string, zone: FormZone | null, imported: number | null): string {
  const parts = [formatWeekdayMonthDay(today)];
  if (zone !== null) parts.push(`FORM ZONE: ${ZONE_LABELS[zone]}`);
  if (imported !== null) parts.push(`${formatInt(imported)} ${plural(imported, 'RIDE')} IMPORTED`);
  return parts.join(' · ');
}
